import {
  ErrorComponent,
  Link,
  rootRouteId,
  useMatch,
  useRouter,
} from '@tanstack/react-router'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { isStaleChunkError, reloadOnceForStaleChunk } from '../utilities/staleChunkReload'
import { reportRenderError } from '../utilities/errorReporter'

export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
  const router = useRouter()
  const isRoot = useMatch({
    strict: false,
    select: (state) => state.id === rootRouteId,
  })

  // A chunk missing after a deploy lands here when a lazy route or component fails to import.
  // Reload once onto the new build; if that already happened, fall through to the error below.
  const stale = isStaleChunkError(error)
  const [reloading, setReloading] = useState(stale)
  useEffect(() => {
    if (stale) {
      setReloading(reloadOnceForStaleChunk())
    }
  }, [stale])

  // The user sees this screen; the log must see the error behind it.
  useEffect(() => {
    if (!stale) reportRenderError(error)
  }, [error, stale])

  if (reloading) {
    return null
  }

  console.error('DefaultCatchBoundary Error:', error)

  return (
    <div className="min-w-0 flex-1 p-4 flex flex-col items-center justify-center gap-6">
      <ErrorComponent error={error} />
      <div className="flex gap-2 items-center flex-wrap">
        <button
          onClick={() => {
            router.invalidate()
          }}
          className={`px-2 py-1 bg-gray-600 dark:bg-gray-700 rounded-sm text-white uppercase font-extrabold`}
        >
          Try Again
        </button>
        {isRoot ? (
          <Link
            to="/"
            className={`px-2 py-1 bg-gray-600 dark:bg-gray-700 rounded-sm text-white uppercase font-extrabold`}
          >
            Home
          </Link>
        ) : (
          <Link
            to="/"
            className={`px-2 py-1 bg-gray-600 dark:bg-gray-700 rounded-sm text-white uppercase font-extrabold`}
            onClick={(e) => {
              e.preventDefault()
              window.history.back()
            }}
          >
            Go Back
          </Link>
        )}
      </div>
    </div>
  )
}
